import { MaskedInput } from '@/components/masked-input'
import { isValidPhone } from '@/helpers/phone-validation'
import { validateCpf } from '@/helpers/validations'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

type AddCollaboratorDialogProps = DialogProps & {
  onClose: () => void
  onAddCollaborator: (data: AddCollaboratorFormData) => void
  isLoading: boolean
}

const addCollaboratorFormSchema = z.object({
  name: z.string().min(1, 'Obrigatório'),
  tax_id: z.string().min(1, 'Obrigatório').refine(validateCpf, 'CPF inválido'),
  email: z.string().min(1, 'Obrigatório').email('E-mail inválido'),
  phone: z
    .string()
    .min(1, 'Obrigatório')
    .refine(isValidPhone, 'Telefone inválido'),
})

export type AddCollaboratorFormData = z.infer<typeof addCollaboratorFormSchema>

export function AddCollaboratorDialog({
  onAddCollaborator,
  isLoading,
  ...props
}: AddCollaboratorDialogProps) {
  const { control, handleSubmit } = useForm<AddCollaboratorFormData>({
    resolver: zodResolver(addCollaboratorFormSchema),
    defaultValues: {
      name: '',
      tax_id: '',
      email: '',
      phone: '',
    },
  })

  return (
    <Dialog
      {...props}
      component="form"
      onSubmit={handleSubmit(onAddCollaborator)}
    >
      <DialogTitle>Convidar colaborador</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Nome completo"
                error={Boolean(error)}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name="tax_id"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MaskedInput
                {...field}
                label="CPF"
                mask="000.000.000-00"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
 
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="E-mail"
                error={Boolean(error)}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MaskedInput
                {...field}
                label="Telefone"
                type="tel"
                mask="(00) 00000-0000"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={props.onClose}>
          Cancelar
        </Button>
        <LoadingButton type="submit" loading={isLoading}>
          Convidar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
